import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  Badge,
  Button,
  Dialog,
  EmptyState,
  ErrorState,
  Input,
  LoadingState,
  ModuleNotInPlanState,
  Toast,
} from '@assist/ui';
import { ApiError, endpoints, type FindingSummary } from '../state/api.js';
import { useSession } from '../state/session.js';
import { formatDateShort, formatNumber, isOverdue } from '../lib/format.js';
import { applyBulkDueDate } from '../lib/bulk.js';

const STATES = ['OPEN', 'ASSIGNED', 'IN_PROGRESS', 'AWAITING_VERIFICATION', 'SENT_BACK', 'CLOSED'];

/**
 * Layar Findings List — Figma `8:2` · M02 · rute `/findings` · izin LDR MGR AUD.
 *
 * Lima kondisi peta §5.2, semuanya dibangun:
 *   baku · kosong (dengan ajakan membuat temuan) · memuat · gagal ·
 *   hasil saringan kosong — dengan TEKS BERBEDA dari kosong murni, karena kedua
 *   keadaan itu menuntut tindakan yang berbeda dari pengguna.
 *
 * Ditambah kondisi keenam yang berlaku lintas modul: modul di luar paket
 * (`70:1007`), yang bukan halaman galat.
 */
export function FindingsList({
  onOpen,
  onNavigate,
  initialQuery,
}: {
  onOpen: (code: string) => void;
  /** Dipakai tombol `New finding` — Figma `8:2`, tombol primer di kepala halaman. */
  onNavigate: (route: string) => void;
  initialQuery?: string;
}) {
  const { t, locale, hasModule, me } = useSession();
  const [items, setItems] = useState<FindingSummary[]>([]);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [error, setError] = useState<ApiError | null>(null);
  const [stateFilter, setStateFilter] = useState<string>(
    new URLSearchParams(initialQuery ?? '').get('state') ?? '',
  );
  /**
   * Yang dipilih disimpan sebagai KODE (`TMN-0001`), bukan sebagai ULID.
   *
   * UAT D-03: aksi massalnya harus benar-benar memanggil
   * `POST /v1/findings/{code}/transitions`, dan rute itu memakai kode yang dilihat
   * pengguna. Menyimpan ULID berarti menerjemahkan dua arah setiap kali, dan
   * bentuk sebelumnya menyimpan ULID justru karena tidak ada yang pernah dikirim.
   */
  const [selected, setSelected] = useState<Set<string>>(new Set());
  /**
   * PAGINASI — audit L-03 (BERAT).
   *
   * Tangkapan `03-findings-list-en.png`: 30 baris sekaligus, halaman setinggi 3106 px,
   * tanpa satu pun kendali halaman. Figma `8:2` menampilkan TUJUH baris, lalu
   * `Showing 1–7 of 32 findings` dan paginasi `‹ 1 2 3 4 5 ›`.
   *
   * Tujuh bukan angka yang dikarang: ia yang membuat halaman muat dalam 900 px bersama
   * kepala, saringan, dan kakinya. Paginasinya DI KLIEN, atas baris yang sudah dimuat —
   * rute `GET /v1/findings` sudah membatasi jumlahnya di server, dan menambah paginasi
   * server adalah perubahan kontrak yang bukan bagian dari perbaikan kepatuhan ini.
   */
  const [page, setPage] = useState(1);
  const [confirmBulk, setConfirmBulk] = useState(false);
  const [bulkDue, setBulkDue] = useState('');
  const [bulkReason, setBulkReason] = useState('');
  const [bulkBusy, setBulkBusy] = useState(false);
  const [toast, setToast] = useState<{ kind: 'success' | 'warning' | 'danger'; text: string } | null>(
    null,
  );

  const entitled = hasModule('M02');
  const PER_PAGE = 7;

  const load = useCallback(async () => {
    setStatus('loading');
    setError(null);
    try {
      const params = new URLSearchParams(initialQuery ?? '');
      if (stateFilter) params.set('state', stateFilter);
      else params.delete('state');
      const payload = await endpoints.findings(params.toString());
      setItems(payload.items);
      setStatus('ready');
    } catch (err) {
      setError(err instanceof ApiError ? err : null);
      setStatus('error');
    }
  }, [stateFilter, initialQuery]);

  useEffect(() => {
    if (entitled) void load();
  }, [entitled, load]);

  const filtered = useMemo(() => items, [items]);
  /**
   * APAKAH LAYAR INI SEDANG MENYARING — dari SELURUH parameter, bukan dari `state` saja.
   *
   * UAT D-11 / U2-22 (masih terbuka dua gelombang). Kartu "Lewat tenggat" pada Dasbor
   * menuju `/findings?overdue=true`; API menghormati saringan itu dan mengembalikan nol
   * baris ketika memang tidak ada yang lewat tenggat. Baris ini dulu berbunyi
   * `stateFilter !== ''`, sehingga saringan `overdue` — dan setiap saringan lain yang
   * datang dari URL — TIDAK DIKENALI, dan layarnya menampilkan kondisi kosong MURNI:
   *
   *     "Belum ada temuan — Laporan pertama dari lapangan akan muncul di sini"
   *
   * beserta ajakan "Lapor temuan". Manajer yang mengklik "Lewat tenggat" karena itu
   * membaca bahwa organisasinya belum pernah punya temuan sama sekali.
   *
   * Yang dihitung sekarang adalah SETIAP parameter saringan yang benar-benar ada di URL,
   * bukan daftar nama yang harus diingat orang berikutnya: satu parameter baru di
   * `GET /v1/findings` otomatis ikut. `limit` dan `cursor` dikecualikan — keduanya
   * paginasi, bukan saringan, dan sebuah halaman kedua yang kosong bukan "tidak ada
   * temuan".
   */
  /**
   * Tiga angka subjudul Figma, dihitung dari baris yang ADA — bukan dikarang.
   *
   * `avgDays` memakai temuan yang benar-benar SELESAI di kumpulan ini; bila belum ada
   * satu pun yang selesai ia bertanda em dash, bukan nol. Nol hari rata-rata sampai
   * selesai adalah kalimat yang tidak pernah benar.
   */
  const ringkas = useMemo(() => {
    const open = items.filter((i) => i.state !== 'CLOSED').length;
    const overdue = items.filter((i) => isOverdue(i.due_at, i.closed_at)).length;
    const closed = items.filter((i) => i.closed_at !== null);
    const days =
      closed.length === 0
        ? null
        : closed.reduce(
            (acc, i) =>
              acc +
              (new Date(i.closed_at as string).getTime() - new Date(i.opened_at).getTime()) /
                86_400_000,
            0,
          ) / closed.length;
    return {
      open,
      overdue,
      avgDays: days === null ? '—' : days.toFixed(1),
    };
  }, [items]);

  const isFiltering = useMemo(() => {
    if (stateFilter !== '') return true;
    const params = new URLSearchParams(initialQuery ?? '');
    for (const [nama, nilai] of params) {
      if (nilai === '' || nama === 'limit' || nama === 'cursor') continue;
      return true;
    }
    return false;
  }, [stateFilter, initialQuery]);

  /**
   * Menyusun CSV dari baris yang terlihat dan menyerahkannya ke peramban.
   *
   * Tanpa dependensi: `Blob` + `URL.createObjectURL` + satu `<a download>`. Nilai
   * di-quote dan tanda kutipnya digandakan — sebuah judul temuan yang memuat koma atau
   * tanda kutip adalah hal biasa di lantai pabrik, dan CSV yang rusak karenanya akan
   * dibuka di Excel sebagai satu kolom.
   *
   * BOM UTF-8 di depan: tanpanya, Excel di Windows membaca `Rapi (Seiton)` sebagai
   * mojibake, dan berkas yang tidak dapat dibaca sama saja dengan berkas yang tidak
   * pernah terunduh.
   */
  function exportCsv() {
    const kolom: Array<[string, (f: FindingSummary) => string]> = [
      [t('findings.column.code'), (f) => f.code ?? ''],
      [t('findings.column.title'), (f) => f.title],
      [t('findings.column.state'), (f) => t(`state.${f.state}`)],
      [t('findings.column.owner'), (f) => f.owner?.full_name ?? ''],
      [t('findings.column.area'), (f) => f.location?.code ?? ''],
      [t('findings.column.due'), (f) => f.due_at ?? ''],
    ];
    const kutip = (v: string) => `"${v.replace(/"/g, '""')}"`;
    const baris = [
      kolom.map(([judul]) => kutip(judul)).join(','),
      ...filtered.map((f) => kolom.map(([, ambil]) => kutip(ambil(f))).join(',')),
    ];
    const blob = new Blob(['\ufeff' + baris.join('\r\n') + '\r\n'], {
      type: 'text/csv;charset=utf-8',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `temuan-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  async function runBulkDueDate() {
    setBulkBusy(true);
    try {
      const hasil = await applyBulkDueDate(
        { transition: (code, body) => endpoints.transition(code, body as never) },
        [...selected],
        bulkDue,
        bulkReason,
      );
      setConfirmBulk(false);
      setBulkDue('');
      setBulkReason('');
      if (hasil.gagal.length === 0) {
        setSelected(new Set());
        setToast({ kind: 'success', text: t('findings.bulk.done', { count: hasil.berhasil.length }) });
      } else {
        // Kegagalan sebagian DIKATAKAN, dengan nama temuannya. Membulatkannya
        // menjadi "berhasil" adalah D-03 dalam ukuran yang lebih kecil.
        setSelected(new Set(hasil.gagal.map((g) => g.code)));
        setToast({
          kind: 'warning',
          text: t('findings.bulk.partial', {
            done: hasil.berhasil.length,
            failed: hasil.gagal.length,
            codes: hasil.gagal.map((g) => g.code).join(', '),
          }),
        });
      }
      await load();
    } catch (err) {
      // Penolakan dini dibawa sebagai KODE (`BulkRefused`), jadi kalimatnya dirender
      // di sini — dalam bahasa pembacanya, bukan dalam bahasa penulis kodenya.
      const kode = (err as { problem?: { code?: string } }).problem?.code ?? 'INTERNAL';
      setToast({ kind: 'danger', text: t(`error.${kode}`, { required: 1, found: 0 }) });
    } finally {
      setBulkBusy(false);
    }
  }

  // Modul di luar paket. BUKAN halaman galat: ia menjelaskan modul apa dan siapa
  // yang dapat menyalakannya, karena yang melihatnya biasanya bukan pembelinya.
  if (!entitled) {
    return (
      <section className="page">
        <h1 className="page__title">{t('findings.title')}</h1>
        {/* UAT D-08: `organisation` dulu dikirim KOSONG, menyisakan "…belum
            dinyalakan untuk ." — kalimat yang terputus tepat sebelum titik, di
            kedua bahasa. Nama organisasi ada di sesi (`GET /v1/me` → `tenant.name`);
            bila sesi memang tidak punya tenant, kalimat tanpa namanya yang dipakai
            — bukan nama kosong. */}
        <ModuleNotInPlanState
          title={t('state.notInPlan.title')}
          body={
            me?.tenant?.name
              ? t('state.notInPlan.body', { module: 'M02', organisation: me.tenant.name })
              : t('state.notInPlan.bodyNoOrg', { module: 'M02' })
          }
          whoCanHelp={t('state.notInPlan.whoCanHelp')}
        />
      </section>
    );
  }

  const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  const halaman = Math.min(page, totalPages);
  const mulai = (halaman - 1) * PER_PAGE;
  const terlihat = filtered.slice(mulai, mulai + PER_PAGE);

  return (
    <section className="page">
      <header className="page__head">
        <div>
          {/* Judul dua warna, dua kunci — urutannya membalik antarbahasa
              (`Open`+`Findings` versus `Temuan`+`Terbuka`). Audit L-03 dan §5. */}
          <h1 className="page__title">
            {t('findings.title')}{' '}
            <span className="page__title-accent">{t('findings.titleAccent')}</span>
          </h1>
          {/* Subjudul Figma `8:2`: berapa terbuka, berapa lewat tenggat, berapa hari
              rata-rata sampai selesai — tiga angka yang menjawab "seberapa sehat", bukan
              satu angka yang mengulang jumlah baris di bawahnya. */}
          <p className="page__subtitle">
            {t('findings.subtitle', {
              open: ringkas.open,
              overdue: ringkas.overdue,
              days: ringkas.avgDays,
            })}
          </p>
        </div>
        <div className="page__actions">
          {/*
            EKSPOR CSV YANG BENAR-BENAR MENGUNDUH — UAT D-15 / U2-26, dua gelombang
            terbuka. Tombolnya dulu `onClick={() => void 0}`: nol permintaan, nol
            unduhan, nol perubahan layar. Sebuah tombol mati tanpa umpan balik membuat
            orang menekannya berulang kali lalu menyimpulkan produknya rusak.

            Yang diekspor adalah BARIS YANG SEDANG TERLIHAT, bukan seluruh basis data,
            dan itu keputusan yang perlu dinyatakan: sebuah ekspor "semuanya" menuntut
            paginasi server dan rute ekspor tersendiri (`report_run`, M11), dan
            menjanjikannya lewat tombol ini akan mengirim manajer pulang dengan berkas
            yang berisi 50 dari 8.000 baris tanpa cara tahu. Judul kolomnya diambil dari
            katalog, jadi berkasnya pun dua bahasa.
          */}
          <Button variant="secondary" onClick={exportCsv} disabled={filtered.length === 0}>
            {t('findings.export')}
          </Button>
          {/*
            `New finding` PRIMER — Figma `8:2`. Ia membawa ke layar Lapor Temuan, yang
            ADA sebagai rute konsol (`/findings/new`). Sebelumnya layar ini tidak punya
            satu pun jalan membuat temuan, sehingga satu-satunya cara adalah mengetik
            alamatnya — dan itulah kenapa keadaan kosongnya pun tidak berani mengajak.
          */}
          {me?.role_code !== 'AUD' ? (
            <Button variant="primary" onClick={() => onNavigate('/findings/new')}>
              {t('findings.new')}
            </Button>
          ) : null}
        </div>
      </header>

      {/*
        CHIP SARINGAN BERHITUNG — Figma `8:2`: `All 32 · Open 18 · In progress 9 ·
        Awaiting verification 5`, masing-masing dengan angkanya. Kode sebelumnya
        menampilkan tujuh chip TANPA satu angka pun, dan tanpa angka sebuah saringan
        tidak dapat dipakai untuk memutuskan: orang menekannya satu per satu untuk
        mengetahui mana yang berisi.

        Angkanya dihitung dari baris yang SUDAH dimuat, jadi ia tidak menambah satu
        permintaan pun — dan ia konsisten dengan tabel di bawahnya karena berasal dari
        larik yang sama.
      */}
      <div className="filters" role="group" aria-label={t('findings.filter.state')}>
        <button
          type="button"
          className={stateFilter === '' ? 'is-active' : ''}
          aria-pressed={stateFilter === ''}
          onClick={() => {
            setStateFilter('');
            setPage(1);
          }}
        >
          {t('findings.filter.all')}
          <span className="filters__count">{formatNumber(items.length, locale)}</span>
        </button>
        {STATES.map((s) => (
          <button
            key={s}
            type="button"
            className={stateFilter === s ? 'is-active' : ''}
            aria-pressed={stateFilter === s}
            onClick={() => {
              setStateFilter(s);
              setPage(1);
            }}
          >
            {t(`state.${s}`)}
            <span className="filters__count">
              {formatNumber(items.filter((i) => i.state === s).length, locale)}
            </span>
          </button>
        ))}
        {isFiltering ? (
          <Button variant="text" onClick={() => setStateFilter('')}>
            {t('findings.filter.clear')}
          </Button>
        ) : null}
      </div>

      {selected.size > 0 ? (
        <div className="bulkbar">
          <span>{t('findings.selected', { count: selected.size })}</span>
          {/* Aksi massal SELALU lewat dialog konfirmasi (peta §5.2). */}
          <Button variant="secondary" onClick={() => setConfirmBulk(true)}>
            {t('findings.bulk.changeDue')}
          </Button>
        </div>
      ) : null}

      {status === 'loading' ? <LoadingState rows={6} label={t('state.loading.title')} /> : null}

      {status === 'error' ? (
        <ErrorState
          title={t('state.error.title')}
          body={t('state.error.body')}
          code={error?.problem.request_id ?? null}
          codeLabel={
            error ? t('state.error.code', { code: error.problem.request_id ?? error.problem.code }) : undefined
          }
          retryLabel={t('app.retry')}
          onRetry={() => void load()}
        />
      ) : null}

      {/*
        Kosong murni dan hasil saringan kosong adalah DUA kondisi berbeda.

        TANPA ajakan "Lapor temuan" — UAT D-15 / U2-26. Tombol itu dulu
        `onClick={() => void 0}`, dan ia tidak dapat diperbaiki di konsol: melaporkan
        temuan adalah alur aplikasi LAPANGAN (alur 1 §6), yang menuntut kamera dan yang
        layarnya belum dibangun. Sebuah ajakan yang tidak dapat dipenuhi lebih buruk
        daripada tidak ada ajakan, jadi yang tersisa adalah kalimatnya saja.
      */}
      {status === 'ready' && filtered.length === 0 && !isFiltering ? (
        <EmptyState title={t('findings.empty.title')} body={t('findings.empty.body')} />
      ) : null}
      {status === 'ready' && filtered.length === 0 && isFiltering ? (
        <EmptyState
          title={t('findings.emptyFiltered.title')}
          body={t('findings.emptyFiltered.body')}
          action={{ label: t('findings.filter.clear'), onClick: () => setStateFilter('') }}
        />
      ) : null}

      {status === 'ready' && filtered.length > 0 ? (
        <table className="table">
          <thead>
            <tr>
              <th scope="col" className="table__check">
                {/*
                  UAT D-17 / U2-29: judul kolom khusus pembaca layar berbunyi "0 dipilih"
                  sementara satu baris tercentang, karena `count: 0` ditulis tetap. Chip
                  di atas tabel sudah benar; yang ini tidak, dan hanya pembaca layar yang
                  mendengarnya — yaitu pengguna yang paling tidak punya cara memeriksanya
                  sendiri.
                */}
                <span className="assist-sr-only">
                  {t('findings.selected', { count: selected.size })}
                </span>
              </th>
              {/* SATU kolom `Findings`, bukan dua. Figma `8:2` memotong judul dengan
                  elipsis dalam SATU baris dan menaruh kodenya sebagai baris kedua di
                  bawahnya. Dua kolom terpisah memaksa judul membungkus 2-5 baris, dan
                  itu yang menggandakan tinggi setiap barisnya (audit L-03). */}
              <th scope="col">{t('findings.column.title')}</th>
              <th scope="col">{t('findings.column.category')}</th>
              <th scope="col">{t('findings.column.area')}</th>
              {/* Kolom `Priority` DIKEMBALIKAN — ia ada di Figma `8:2` dan hilang di
                  kode, sementara `Photos` dan kotak centang ditambahkan. Kotak centang
                  DIPERTAHANKAN: aksi massal tenggat adalah fungsi yang sudah dibangun
                  dan diuji (UAT D-03), dan menghapusnya karena Figma belum menggambarnya
                  akan membuang pekerjaan yang benar. */}
              <th scope="col">{t('findings.column.priority')}</th>
              <th scope="col">{t('findings.column.owner')}</th>
              <th scope="col">{t('findings.column.due')}</th>
              <th scope="col">{t('findings.column.state')}</th>
              <th scope="col">{t('findings.column.photos')}</th>
            </tr>
          </thead>
          <tbody>
            {terlihat.map((item) => {
              const overdue = isOverdue(item.due_at, item.closed_at);
              return (
                <tr key={item.id} className={overdue ? 'is-overdue' : ''}>
                  <td className="table__check">
                    <input
                      type="checkbox"
                      aria-label={item.code ?? item.id}
                      disabled={item.code === null}
                      checked={item.code !== null && selected.has(item.code)}
                      onChange={(e) => {
                        if (item.code === null) return;
                        const next = new Set(selected);
                        if (e.target.checked) next.add(item.code);
                        else next.delete(item.code);
                        setSelected(next);
                      }}
                    />
                  </td>
                  <td className="cell--finding">
                    <button
                      className="linkish cell--finding-title"
                      type="button"
                      title={item.title}
                      onClick={() => item.code && onOpen(item.code)}
                    >
                      {item.title}
                    </button>
                    {/* Kode sebagai baris kedua. Lencana Safety TIDAK diulang di sini:
                        kolom Kategori sudah membawanya, dan dua lencana merah untuk satu
                        fakta adalah dua kali kebisingan untuk nol informasi tambahan. */}
                    <span className="cell--finding-code">{item.code ?? '—'}</span>
                  </td>
                  {/* Kategori sebagai LENCANA berwarna — Figma `8:2`. */}
                  <td>
                    {item.category_code ? (
                      <Badge tone={item.category_code === 'SAFETY' ? 'danger' : 'information'}>
                        {t(`category.${item.category_code}`)}
                      </Badge>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="cell--nowrap">{item.location?.code ?? '—'}</td>
                  {/* Prioritas sebagai TEKS BERWARNA, bukan lencana: Figma menulisnya
                      polos, dan tiga lencana per baris membuat tabel menjadi dinding
                      warna tempat status yang sesungguhnya tidak lagi menonjol. */}
                  <td className={`cell--priority is-${(item.severity ?? 'LOW').toLowerCase()}`}>
                    {item.severity ? t(`findings.priority.${item.severity}`) : '—'}
                  </td>
                  {/* Pemilik = avatar 26 px + nama, Figma `8:2`. Nama polos membuat
                      kolom ini terbaca sebagai teks, bukan sebagai orang. */}
                  <td>
                    {item.owner?.full_name ? (
                      <span className="cell--owner">
                        <Avatar name={item.owner.full_name} size={26} />
                        {item.owner.full_name}
                      </span>
                    ) : (
                      <span className="cell--owner-none">{t('findings.unassigned')}</span>
                    )}
                  </td>
                  {/* `20 Sep`, satu baris — Figma `8:2`. `Sep 30, 2026` membungkus tiga
                      baris di kolom selebar ini, dan tiga baris tanggal menggandakan
                      tinggi setiap barisnya (audit L-03). Merah bila terlambat. */}
                  <td className={overdue ? 'cell--overdue' : ''}>
                    {item.due_at ? formatDateShort(item.due_at, locale) : t('findings.noDueDate')}
                  </td>
                  {/*
                    SATU LENCANA PER BARIS — audit L-03 dan K-02.

                    Bentuk sebelumnya menumpuk lencana state DAN setiap fasetnya, sehingga
                    satu baris dapat memuat tiga lencana dan teksnya menjadi kalimat
                    ("Dikembalikan, dan berhenti di situ" — 34 karakter, ± 260 px, 2,4x
                    lebar maksimum yang dirancang). Faset `OVERDUE` kini dibawa KOLOM
                    TENGGAT yang memerah — tempat yang memang menjawab "kapan" — dan
                    faset lain dibawa atribut `title`, bukan lencana kedua.
                  */}
                  <td>
                    <Badge badgeTone={toneOf(item.state)}>{t(`state.${item.state}`)}</Badge>
                    {item.facets.filter((f) => f !== 'OVERDUE').length > 0 ? (
                      <span
                        className="cell--facets"
                        title={item.facets
                          .filter((f) => f !== 'OVERDUE')
                          .map((f) => t(`facet.${f}`))
                          .join(' · ')}
                      >
                        {' '}
                        +{item.facets.filter((f) => f !== 'OVERDUE').length}
                      </span>
                    ) : null}
                  </td>
                  <td>{item.photo_count}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : null}

      {/* Penghitung dan paginasi — Figma `8:2`. Keduanya SELALU tampil ketika ada
          baris, termasuk ketika hanya ada satu halaman: penghitung "Menampilkan 1–5
          dari 5" adalah jawaban yang berguna, dan menyembunyikannya membuat pengguna
          bertanya apakah masih ada yang tersembunyi. */}
      {status === 'ready' && filtered.length > 0 ? (
        <nav className="pagination" aria-label={t('findings.pagination.next')}>
          <p className="pagination__count">
            {t('findings.pagination.showing', {
              from: mulai + 1,
              to: Math.min(mulai + PER_PAGE, filtered.length),
              total: filtered.length,
            })}
          </p>
          {totalPages > 1 ? (
            <div className="pagination__pages">
              <Button
                variant="secondary"
                disabled={halaman === 1}
                onClick={() => setPage(halaman - 1)}
              >
                ‹<span className="assist-sr-only">{t('findings.pagination.previous')}</span>
              </Button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
                <Button
                  key={n}
                  variant={n === halaman ? 'primary' : 'secondary'}
                  aria-current={n === halaman ? 'page' : undefined}
                  onClick={() => setPage(n)}
                >
                  {n}
                  <span className="assist-sr-only">
                    {' '}
                    {t('findings.pagination.page', { page: n })}
                  </span>
                </Button>
              ))}
              <Button
                variant="secondary"
                disabled={halaman === totalPages}
                onClick={() => setPage(halaman + 1)}
              >
                ›<span className="assist-sr-only">{t('findings.pagination.next')}</span>
              </Button>
            </div>
          ) : null}
        </nav>
      ) : null}

      {/* UAT D-03. Yang dulu ada di sini: dialog TANPA medan tanggal, dan
          `onConfirm` yang hanya menutupnya — nol permintaan, nol perubahan, nol
          galat, nol toast. Sekarang ia mengumpulkan tenggat dan alasannya,
          mengirimkannya per temuan, lalu MENGATAKAN hasilnya — termasuk kegagalan
          sebagian. Ia juga bukan lagi dialog "tidak dapat dibatalkan" (D-16):
          memindahkan tenggat dapat dipindahkan kembali. */}
      <Dialog
        open={confirmBulk}
        kind="confirm"
        title={t('findings.bulk.changeDue')}
        description={t('findings.bulk.changeDue.describe', { count: selected.size })}
        confirmLabel={t('app.save')}
        cancelLabel={t('app.cancel')}
        busy={bulkBusy}
        confirmDisabled={bulkDue.trim() === '' || bulkReason.trim() === ''}
        onConfirm={() => void runBulkDueDate()}
        onCancel={() => {
          setConfirmBulk(false);
          setBulkDue('');
          setBulkReason('');
        }}
      >
        <Input
          label={t('finding.dueDate.new')}
          inputType="date"
          value={bulkDue}
          min={new Date().toISOString().slice(0, 10)}
          onChange={(e) => setBulkDue(e.target.value)}
          error={bulkDue.trim() === '' ? t('error.DUE_DATE_IN_PAST') : null}
        />
        <Input
          label={t('finding.reason')}
          value={bulkReason}
          onChange={(e) => setBulkReason(e.target.value)}
          error={bulkReason.trim() === '' ? t('error.REASON_REQUIRED') : null}
        />
      </Dialog>

      {toast ? (
        <div className="assist-toast-stack">
          <Toast
            kind={toast.kind}
            signalWord={
              toast.kind === 'success'
                ? t('signal.success')
                : toast.kind === 'warning'
                  ? t('signal.warning')
                  : t('signal.danger')
            }
            title={toast.text}
            onDismiss={() => setToast(null)}
            closeLabel={t('app.dismiss')}
          />
        </div>
      ) : null}
    </section>
  );
}

/** Nada lencana per state, mengikuti `badge_tone` di `workflows/finding.v1.json`. */
function toneOf(state: string): string {
  switch (state) {
    case 'CLOSED':
      return 'SUCCESS';
    case 'AWAITING_VERIFICATION':
    case 'SENT_BACK':
      return 'WARNING';
    default:
      return 'INFORMATION';
  }
}
